/**
 * Icon loading utilities with fallback support
 */

import { DEFAULT_CATEGORY_ICON } from './constants';

// Cache for validated icon URLs
const validatedIconCache = new Map<string, string>();
const failedIconCache = new Set<string>();

// Default fallback icon (data URL that always works)
const FALLBACK_ICON = DEFAULT_CATEGORY_ICON;

/**
 * Preload an image and validate it loads successfully with retry logic
 * Returns the original URL if successful, or fallback URL if it fails after retries
 */
export async function preloadIcon(url: string, fallbackUrl: string = FALLBACK_ICON, maxRetries: number = 3): Promise<string> {
    // Check cache first
    if (validatedIconCache.has(url)) {
        return validatedIconCache.get(url) ?? fallbackUrl;
    }

    // If previously failed, return fallback immediately
    if (failedIconCache.has(url)) {
        return fallbackUrl;
    }

    // Skip validation for data URLs (they're embedded and always work)
    if (url.startsWith('data:')) {
        validatedIconCache.set(url, url);
        return url;
    }

    // Try loading with retries
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        const result = await new Promise<{ success: boolean; url: string }>((resolve) => {
            const img = new Image();
            const timeout = setTimeout(() => {
                console.warn(`Icon loading timeout (attempt ${attempt}/${maxRetries}): ${url}`);
                resolve({ success: false, url: fallbackUrl });
            }, 2500); // 2.5 second timeout per attempt

            img.onload = () => {
                clearTimeout(timeout);
                resolve({ success: true, url });
            };

            img.onerror = () => {
                clearTimeout(timeout);
                console.warn(`Failed to load icon (attempt ${attempt}/${maxRetries}): ${url}`);
                resolve({ success: false, url: fallbackUrl });
            };

            img.src = url;
        });

        if (result.success) {
            validatedIconCache.set(url, url);
            return url;
        }

        // Wait before retrying (exponential backoff: 500ms, 1000ms, 2000ms)
        if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 500 * Math.pow(2, attempt - 1)));
        }
    }

    // All attempts failed
    console.warn(`Failed to load icon after ${maxRetries} attempts: ${url}, using fallback`);
    failedIconCache.add(url);
    return fallbackUrl;
}

/**
 * Preload multiple icons in parallel
 * Returns a map of original URL to validated URL (or fallback)
 */
export async function preloadIcons(urls: string[], fallbackUrl: string = FALLBACK_ICON, maxRetries: number = 3): Promise<Map<string, string>> {
    const results = await Promise.all(
        urls.map(async (url) => {
            const validatedUrl = await preloadIcon(url, fallbackUrl, maxRetries);
            return [url, validatedUrl] as [string, string];
        })
    );

    return new Map(results);
}

/**
 * Get a validated icon URL with async validation (waits for load to complete)
 * Use this for custom icons to ensure failed URLs return fallback before marker creation
 */
export async function getValidatedIconAsync(url: string, fallbackUrl: string = FALLBACK_ICON): Promise<string> {
    // Check cache first
    if (validatedIconCache.has(url)) {
        return validatedIconCache.get(url) ?? fallbackUrl;
    }

    if (failedIconCache.has(url)) {
        return fallbackUrl;
    }

    // Validate URL format upfront - if malformed, return fallback immediately
    if (!isValidIconUrl(url)) {
        console.warn(`Invalid icon URL format: ${url}, using fallback`);
        failedIconCache.add(url);
        return fallbackUrl;
    }

    // Wait for preload to complete, will return validated URL or fallback
    return await preloadIcon(url, fallbackUrl);
}

/**
 * Get a validated icon URL from cache, or return fallback
 * Use this for synchronous access after preloading
 */
export function getValidatedIcon(url: string, fallbackUrl: string = FALLBACK_ICON): string {
    // Check cache first - they should be here
    if (validatedIconCache.has(url)) {
        return validatedIconCache.get(url) ?? fallbackUrl;
    }

    if (failedIconCache.has(url)) {
        return fallbackUrl;
    }

    // Validate URL format upfront - if malformed, return fallback immediately
    if (!isValidIconUrl(url)) {
        console.warn(`Invalid icon URL format: ${url}, using fallback`);
        failedIconCache.add(url);
        return fallbackUrl;
    }

    // If not in cache but valid format, start try loading directly
    return url; // Let Google Maps try to load it. We should have preloaded it already, so this is a fallback for any edge cases where it wasn't cached properly.
}

/**
 * Clear the icon cache (useful for testing or after updates)
 */
export function clearIconCache(): void {
    validatedIconCache.clear();
    failedIconCache.clear();
}

/**
 * Check if an icon URL is valid without loading it
 */
export function isValidIconUrl(url: string): boolean {
    if (!url || url.trim() === '') {
        return false;
    }

    // Data URLs are always valid
    if (url.startsWith('data:')) {
        return true;
    }

    // Check if it's a valid URL format
    try {
        const parsedUrl = new URL(url, window.location.origin);
        return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
    } catch {
        return false;
    }
}

/**
 * Get cache statistics (for debugging)
 */
export function getIconCacheStats() {
    return {
        validated: validatedIconCache.size,
        failed: failedIconCache.size,
        validatedUrls: Array.from(validatedIconCache.keys()),
        failedUrls: Array.from(failedIconCache),
    };
}
